import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from '@gapartners/ui';
import { api } from '@/store/api';
import { transferSseService } from '@/services/transferSse';
import { useAppDispatch } from '@/store/hooks';
import { setPendingAcceptedWarmConsult } from '@/store/slices/warmTransferSlice';
import type {
  TransferRequestEvent,
  TransferResponseEvent,
} from '@/types/agents';

type PendingRequest = TransferRequestEvent;

export function TransferRequestInbox() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [respondAgentTransfer] = api.useRespondAgentTransferMutation();
  const [fetchWarmContext] = api.useLazyGetActiveTransferContextQuery();
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [inFlightRequestId, setInFlightRequestId] = useState<string | null>(
    null
  );
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const currentRequest = useMemo(
    () => pendingRequests[0] ?? null,
    [pendingRequests]
  );

  useEffect(() => {
    const unsubscribe = transferSseService.subscribe(event => {
      if (event.type === 'transfer_request') {
        setPendingRequests(prev =>
          prev.some(item => item.requestId === event.requestId)
            ? prev
            : [...prev, event]
        );
        if (event.mode === 'warm' && !event.context) {
          void fetchWarmContext({ initialContactId: event.contactId })
            .unwrap()
            .then(warmContext => {
              setPendingRequests(prev =>
                prev.map(item =>
                  item.requestId === event.requestId
                    ? {
                        ...item,
                        fromDisplayName:
                          warmContext.initiatedByDisplayName ??
                          item.fromDisplayName,
                        context: warmContext.context,
                      }
                    : item
                )
              );
            })
            .catch(() => {
              // The request can still be accepted; backend may omit context until the consult call arrives.
            });
        }
        return;
      }

      if (event.type === 'transfer_response') {
        const response = event as TransferResponseEvent;
        setPendingRequests(prev =>
          prev.filter(item => item.requestId !== response.requestId)
        );
      }
    });
    return unsubscribe;
  }, [fetchWarmContext]);

  const isWarmRequest = currentRequest?.mode === 'warm';
  const requesterName =
    currentRequest?.fromDisplayName?.trim() ||
    currentRequest?.fromConnectId ||
    '';

  const handleDecision = async (decision: 'accept' | 'decline') => {
    if (!currentRequest || inFlightRequestId) return;
    try {
      setInFlightRequestId(currentRequest.requestId);
      const response = await respondAgentTransfer({
        requestId: currentRequest.requestId,
        decision,
      }).unwrap();
      if (isWarmRequest && response.decision === 'accept') {
        dispatch(
          setPendingAcceptedWarmConsult({
            initialContactId: currentRequest.contactId,
            requestId: currentRequest.requestId,
            fromConnectId: currentRequest.fromConnectId,
            fromDisplayName: currentRequest.fromDisplayName ?? null,
            context: currentRequest.context ?? null,
          })
        );
      }
      setPendingRequests(prev =>
        prev.filter(item => item.requestId !== currentRequest.requestId)
      );
      setToast({
        message:
          response.decision === 'accept'
            ? isWarmRequest
              ? t(
                  'components.contactCenter.warmTransferRequestAccepted',
                  'Warm consult accepted. You will receive a consult call shortly.'
                )
              : t(
                  'components.contactCenter.transferResponseAccepted',
                  'Transfer accepted.'
                )
            : isWarmRequest
              ? t(
                  'components.contactCenter.warmTransferRequestDeclined',
                  'Warm consult declined.'
                )
              : t(
                  'components.contactCenter.transferResponseDeclined',
                  'Transfer declined.'
                ),
        type: 'success',
      });
    } catch {
      setToast({
        message: t(
          'components.contactCenter.transferRespondFailed',
          'Failed to send transfer response.'
        ),
        type: 'error',
      });
    } finally {
      setInFlightRequestId(null);
    }
  };

  return (
    <>
      <Dialog open={!!currentRequest} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isWarmRequest
            ? t(
                'components.contactCenter.warmTransferIncomingRequestTitle',
                'Incoming warm transfer consult'
              )
            : t(
                'components.contactCenter.transferIncomingTitle',
                'Incoming transfer request'
              )}
        </DialogTitle>
        <DialogContent>
          {currentRequest && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {isWarmRequest
                  ? t(
                      'components.contactCenter.warmTransferIncomingRequestMessage',
                      '{{name}} wants a private consult while the customer stays on hold. Accept to allow them to connect to you.',
                      {
                        name: requesterName,
                      }
                    )
                  : t(
                      'components.contactCenter.transferIncomingMessage',
                      '{{name}} wants to transfer contact {{contactId}}.',
                      {
                        name: requesterName,
                        contactId: currentRequest.contactId,
                      }
                    )}
              </Typography>
              {isWarmRequest && currentRequest.context?.agentNote && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {t(
                      'components.contactCenter.warmTransferAgentNoteTitle',
                      'Note from transferring agent'
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {currentRequest.context.agentNote}
                  </Typography>
                </Box>
              )}
              <Typography variant="caption" color="text.disabled">
                {t(
                  'components.contactCenter.transferExpiresAt',
                  'Expires at: {{time}}',
                  {
                    time: new Date(
                      currentRequest.expiresAt
                    ).toLocaleTimeString(),
                  }
                )}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => void handleDecision('decline')}
            disabled={!!inFlightRequestId}
          >
            {t('components.amazonConnect.decline')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleDecision('accept')}
            disabled={!!inFlightRequestId}
          >
            {t('components.amazonConnect.accept')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        onClose={() => setToast(null)}
        message={toast?.message}
        variant="toast"
        severity={toast?.type ?? 'info'}
        autoHideDuration={3000}
      />
    </>
  );
}
